import Condition from "@editor/items/condition/Condition";
import Effect from "@editor/items/effect/Effect";
import type { ActionItem } from "@editor/items/action/ActionItem";
import type { SimulationRoundItem } from "../simulationRound/SimulationRoundItem";
import type { SimulationModelState } from "../model/ModelState";
import { calculateExpression, type ExpressionValue } from "../expression/expressionFunctions";
import type { RequirementItem } from "../requirement/RequirementItem";
import type { ActionCostItem } from "./ActionCostItem";
import type { ExpressionParseContext } from "../effect/Expression";
import type { Uid } from "@/helpers/utility/Id";

interface OtherActionData {
  effects: Effect[];

  requirements: RequirementItem[];
  requirementConditions: { [requirementId: number]: Condition[] };
}

export class Action implements ActionItem, OtherActionData {
  id: number;
  uid: `action_${number}`;
  type: "action";
  createdAt: number;
  title: string;
  description: string;
  imageId?: number;
  costs: ActionCostItem[];
  maxOccurrences: number | null;

  effects: Effect[];

  effectsVisibility: "as-is" | "visible" | "hidden" | "values-hidden";

  requirements: RequirementItem[];
  requirementConditions: { [requirementId: number]: Condition[] };

  hiddenIfCostsNotCovered: boolean;
  hiddenIfRequirementsNotFulfilled: boolean;
  hiddenIfMaxUsesReached: boolean;

  order: number;
  docs: Record<string, string>;

  constructor(storeData: ActionItem, otherStoreData: OtherActionData) {
    this.id = storeData.id;
    this.uid = storeData.uid;
    this.type = storeData.type;
    this.createdAt = storeData.createdAt;
    this.title = storeData.title;
    this.description = storeData.description;

    this.maxOccurrences = storeData.maxOccurrences;
    this.costs = storeData.costs;

    this.hiddenIfCostsNotCovered = storeData.hiddenIfCostsNotCovered;
    this.hiddenIfRequirementsNotFulfilled = storeData.hiddenIfRequirementsNotFulfilled;
    this.hiddenIfMaxUsesReached = storeData.hiddenIfMaxUsesReached;

    this.effectsVisibility = storeData.effectsVisibility;

    this.requirements = otherStoreData.requirements;
    this.requirementConditions = otherStoreData.requirementConditions;

    this.effects = otherStoreData.effects;

    this.order = 0;
    this.docs = {};
  }

  getFulfilledRequirements(rounds: SimulationRoundItem[]) {
    return this.requirements.filter((requirement) => {
      const conditions = this.requirementConditions[requirement.id];
      return conditions.every((condition) => condition.evaluate(rounds));
    });
  }

  /**
   * Return all conditions that evaluate to false in a round
   */
  getUnfulfilledConditions(rounds: SimulationRoundItem[]) {
    return this.requirements.reduce((unfulfilledConditions, requirement) => {
      const conditions = this.requirementConditions[requirement.id];
      const unfulfilled = conditions.filter(
        (condition) => !condition.evaluate(rounds)
      );
      return unfulfilledConditions.concat(unfulfilled);
    }, [] as Condition[]);
  }

  calculateCosts(parseContext: ExpressionParseContext) {
    const costs = this.costs;
    const calculatedCosts: { [elementUid: Uid<"target" | "resource">]: number } = {};

    for (const cost of costs) {
      const calculated = calculateExpression(cost.expression, parseContext);
      calculatedCosts[cost.elementUid] = calculated.valid
        ? Number(calculated.value)
        : 0;
    }

    return calculatedCosts;
  }

  /**
   * Get all resources that are not enough
   */
  getMissingResources(
    elementValues: { [uid: string]: ExpressionValue },
    costs: { [elementUid: Uid<"target" | "resource">]: number },
  ) {
    const missingCosts = [];

    // check if all costs are available
    for (const key in costs) {
      const elementUid = key as Uid<"target" | "resource">;
      const cost = costs[elementUid];
      const valueGroup = elementValues[elementUid];

      if (valueGroup == null) {
        console.warn(`value ${elementUid} not found (cost for action ${this.id})`);
        continue;
      }

      if (cost < 0) {
        // resources without minValue can be ignored
        const minValue = valueGroup.minValue;
        if (minValue == null) continue;

        const needed = Math.abs(cost);

        const availableValue =
          Math.floor((Number(valueGroup.value) - minValue) * 100) / 100;

        if (availableValue < needed) {
          // save that the action was prevented in this round
          missingCosts.push({
            elementUid: elementUid,
            actionId: this.id,
            availableValue,
            needed
          });
        }
      } else if (cost > 0) {
        // targets with maxValue can be ignored
        const maxValue = valueGroup.maxValue;
        if (maxValue == null) continue;

        const needed = cost;

        const availableValue =
          Math.floor((maxValue - Number(valueGroup.value)) * 100) / 100;

        if (availableValue < needed) {
          // save that the action was prevented in this round
          missingCosts.push({
            elementUid: elementUid,
            actionId: this.id,
            availableValue,
            needed
          });
        }
      }
    }

    return missingCosts;
  }

  /**
   * Check if the action has been played the maximum number of times in a round.
   */
  hasBeenPlayedMaxTimes(round: SimulationRoundItem) {
    const playedCount = round.actionCounts[this.id];
    if (
      playedCount == null ||
      this.maxOccurrences == null ||
      playedCount < this.maxOccurrences
    ) {
      return false;
    }
    return true;
  }

  /**
   * Create an Action instance from the store data.
   * @param cloneModelState Make sure the model state is decoupled from the live model stores.
   * @param id The action id to create the instance for
   * @returns 
   */
  static instanceFromStore(cloneModelState: SimulationModelState, id: number) {
    const actionStoreItem = cloneModelState.actions.items[id];

    const allConditions = Object.values(cloneModelState.conditions.items);

    const effects = Effect.filterByActivator(
      cloneModelState.effects.items,
      actionStoreItem
    ).map((effect) => new Effect(effect));

    const requirements = Object.values(cloneModelState.requirements.items)
      .filter((requirement) => requirement.itemType === "action" && requirement.itemId === id);
    
    const requirementConditions = requirements.reduce(
      (conditionsByRequirementId: { [requirementId: number]: Condition[] }, requirement) => {
        conditionsByRequirementId[requirement.id] = allConditions
          .filter((condition) => condition.srcType === "requirement" && condition.srcId === requirement.id)
          .map((condition) => new Condition(condition));
        return conditionsByRequirementId;
      },
      {}
    );

    // remove all requirements that have no conditions
    for (const requirementId in requirementConditions) {
      if (requirementConditions[requirementId].length === 0) {
        requirements.splice(requirements.findIndex((req) => req.id === Number(requirementId)), 1);
        delete requirementConditions[requirementId];
      }
    }

    return new Action(actionStoreItem, {
      effects: effects,
      requirements,
      requirementConditions
    });
  }
}

/**
 * Clone an object removing any previous references to the original
 */
function clone<T>(object: T): T {
  return JSON.parse(JSON.stringify(object));
}

export default Action;
